(ns ui.editor2.timeline
  (:require [datascript.core :as ds]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.ui.spinner :as spinner]
            [ui.editor2.defaults :as defaults]
            [ui.editor2.schema :as schema]
            [ui.editor2.common :as common]
            [ui.editor2.composition :as composition]
            [ui.editor2.extension-points :as extension-points]
            [ui.editor2.sticker]
            [ui.editor2.thumbnail-generator]
            [ui.editor2.audio-player]
            [ui.editor2.sidebar-panel-ai :as sidebar-panel-ai]
            [ui.openrouter.sidebar-panel :as openrouter-panel]
            [ui.editor2.sidebar-panel-sticker :as sidebar-panel-sticker]
            [ui.editor2.sidebar :as sidebar]
            [ui.editor2.sidebar-images :as sidebar-images]
            [ui.editor2.sidebar-giphy :as sidebar-giphy]
            [ui.editor2.sidebar-hyperframes-templates :as sidebar-hyperframes]
            [ui.editor2.sidebar-captions :as sidebar-captions]
            [ui.editor2.sidebar-emojies :as sidebar-emojies]
            [ui.editor2.sidebar-background :as sidebar-background]
            [ui.editor2.sidebar-text-presets :as sidebar-text-presets]
            [ui.editor2.sidebar-canva :as sidebar-canva]
            [datascript.transit]
            ))

(defn format-time
  "Converts seconds (int or float) to 'mm:ss' string, zero-padded using JS .padStart."
  [seconds]
  (let [total-secs (js/Math.floor seconds)
        mm (js/Math.floor (/ total-secs 60))
        ss (mod total-secs 60)
        mm-str (.padStart (str mm) 2 "0")
        ss-str (.padStart (str ss) 2 "0")]
    (str mm-str ":" ss-str)))

(defn draw-timeline!
  "Draws a timeline of length `total-secs` on a canvas context `ctx` of width `w` and height `h`.
   scale = pixels per second (e.g. 100 px/s)."
  [ctx w h total-secs scale]
  ;; Clear the canvas
  (.clearRect ctx 0 0 w h)

  ;; Background fill (optional if your canvas has a BG color)
  (set! (.-fillStyle ctx) "#111111")
  (.fillRect ctx 0 0 w h)

  ;; Styles
  (set! (.-strokeStyle ctx) "#777777")
  (set! (.-font ctx) "10px sans-serif")
  (set! (.-fillStyle ctx) "#888888")

  ;; We'll draw ticks from 0 to total-secs
  ;; For each second, we draw a major tick & label;
  ;; We'll also draw 9 minor ticks (every 0.1s)
  (doseq [s (range 0 (inc total-secs))]
    (let [x-pos (* s scale) ;; the x pixel for second s
          label (format-time s)]
      ;; Major tick line
      (.beginPath ctx)
      (.moveTo ctx x-pos h)
      (.lineTo ctx x-pos (/ h 8))
      (.stroke ctx)

      ;; Label at major tick
      (.fillText ctx label (+ x-pos 4) 10)

      ;; Minor ticks (0.1 increments)
      (when (< s total-secs)
        (doseq [i (range 1 10)]
          (let [sub-s (+ s (/ i 10.0))
                minor-x-pos (* sub-s scale)]
            (when (< minor-x-pos w)
              (.beginPath ctx)
              (let [tick-size (int (/ h 4))] ;; smaller line
                (.moveTo ctx minor-x-pos h)
                (.lineTo ctx minor-x-pos (- h tick-size))
                (.stroke ctx)))))))))

(defn ms-to-pixel
  [pixel-per-second ms]
  (* (/ ms
        1000)
     pixel-per-second))

(defn q-total-duration
  [db]
  (let [timeline-uuid (common/q-timeline-uuid db)
        timeline (ds/entity db [:timeline/uuid timeline-uuid])
        slide-uuids (:timeline/slides timeline)]
    (reduce + 0
            (map (fn [uuid]
                   (:slide/duration
                     (ds/entity db [:slide/uuid uuid])))
                 slide-uuids))))

(defn get-total-duration
  [state]
  (let [editor2 (:editor2 state)
        total-duration (q-total-duration (:db editor2))]
    total-duration))

(defn get-time-selector-width
  [state]
  (ms-to-pixel (:pixel-per-second (:editor2 state))
               (get-total-duration state)))

(defn get-max-clip-end
  [state]
  (or (ds/q
        '[:find
          (max ?clip-end) .
          :where
          [_ :clip/end ?clip-end]]
        (get-in state
                [:editor2
                 :db]))
      0))

(defn get-timeline-width
  [state]
  (max (get-time-selector-width state)
       (ms-to-pixel
         (:pixel-per-second (:editor2 state))
         (get-max-clip-end state))))

(defn time-selector
  [state]
  [:canvas
   {:width (get-time-selector-width state)
    :height "20"
    :on {:click [[:editor2/click-time-selector]]}
    :replicant/on-render
    (fn [{:keys [replicant/node]}]
      (let [ctx (.getContext node
                             "2d")]
        (draw-timeline! ctx
                        (.-width node)
                        (.-height node)
                        100
                        (get-in state
                                [:editor2
                                 :pixel-per-second]))))}])

(def playhead-svg
  [:svg
   {:width "13",
    :height "12",
    :fill "none",
    :xmlns "http://www.w3.org/2000/svg"}
   [:path
    {:d
     "M2 .5h9A1.5 1.5 0 0 1 12.5 2v3.736a1.5 1.5 0 0 1-.55 1.161l-4.5 3.68a1.5 1.5 0 0 1-1.9 0l-4.5-3.68a1.5 1.5 0 0 1-.55-1.16V2A1.5 1.5 0 0 1 2 .5Z",
     :fill "#fff",
     :stroke "#757575"}]])

(defn timeline-playhead
  [state]
  (let [editor2 (:editor2 state)
        pixel-per-second (:pixel-per-second editor2)
        left (ms-to-pixel pixel-per-second
                          (:playhead-position editor2))]
    [:div {:class ["absolute" "bottom-0" "pointer-events-none" "h-full"]
           :style {:top "0.45rem"
                   :z-index "9999"
                   :transform (str "translateX(calc(-50% + "
                                   left
                                   "px))")
                   }}
     ;; playhead:
     [:div {:class ["sticky" "h-full" "inline-block" "top-0" "box-border" "cursor-grab"]
            :style {:pointer-events "all"}
            :on {:mousedown [[:editor2/drag
                              {:start-offset left
                               :drag :playhead}]]}}
      ;; playhead container:
      [:div {:class ["flex" "items-center" "flex-col" "justify-center" "h-full"]}
       ;; playhead marker:
       [:div
        playhead-svg]
       ;; playhead handle:
       [:div {:class ["bg-white" "h-full"]
              :style {:filter "drop-shadow(0px 0px 8px rgba(117, 117, 117, 0.5))"
                      :transform "translateY(-0.025rem)"
                      :width "0.0625rem"}}]
       ]]]))

(defn icon-svg
  "Returns the hiccup fragment for a given logical icon keyword."
  [type]
  (case type
    :image [:svg.size-4.mr-2
            {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24" :fill "currentColor"}
            [:path {:fill-rule "evenodd" :d "M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z" :clip-rule "evenodd"}]]
    :gif [:svg {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke-width "1.5" :stroke "currentColor"
                :class ["size-4" "mr-2"]}
          [:path
           {:stroke-linecap "round"
            :stroke-linejoin "round"
            :d "M12.75 8.25v7.5m6-7.5h-3V12m0 0v3.75m0-3.75H18M9.75 9.348c-1.03-1.464-2.698-1.464-3.728 0-1.03 1.465-1.03 3.84 0 5.304 1.03 1.464 2.699 1.464 3.728 0V12h-1.5M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"}]]
    :video [:svg.size-4.mr-2 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24" :fill "currentColor"}
            [:path {:d "M4.5 4.5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h8.25a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3H4.5ZM19.94 18.75l-2.69-2.69V7.94l2.69-2.69c.944-.945 2.56-.276 2.56 1.06v11.38c0 1.336-1.616 2.005-2.56 1.06Z"}]]
    :text [:i.fas.fa-font.mr-2]
    :audio [:svg.size-4.mr-2 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24" :fill "currentColor"}
            [:path {:fill-rule "evenodd" :d "M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .658.122Z" :clip-rule "evenodd"}]]
    :sticker [:svg#Flat.mr-2
              {:fill "currentColor"
               :stroke-width "10"
               :stroke "currentColor"
               :width "24"
               :height "24"
               :viewBox "0 0 256 256"
               :class ["size-4"]}
              [:path
               {:d "M128,84a44,44,0,1,0,44,44A44.04978,44.04978,0,0,0,128,84Zm0,80a36,36,0,1,1,36-36A36.04061,36.04061,0,0,1,128,164ZM172,32H84A52.059,52.059,0,0,0,32,84v88a52.059,52.059,0,0,0,52,52h88a52.059,52.059,0,0,0,52-52V84A52.059,52.059,0,0,0,172,32Zm44,140a44.04978,44.04978,0,0,1-44,44H84a44.04978,44.04978,0,0,1-44-44V84A44.04978,44.04978,0,0,1,84,40h88a44.04978,44.04978,0,0,1,44,44ZM188,76a8,8,0,1,1-8-8A8.00917,8.00917,0,0,1,188,76Z"}]]))

(def type->style
  {:image {:background "bg-green-500"
           :border "border-green-500"
           :icon (icon-svg :image)}
   :gif {:background "bg-orange-500"
         :border "border-orange-500"
         :icon (icon-svg :gif)}
   :video {:background "bg-orange-500"
           :border "border-orange-500"
           :icon (icon-svg :video)}
   :text {:background "bg-gray-500"
          :border "border-gray-500"
          :icon (icon-svg :text)}
   :audio {:background "bg-blue-500"
           :border "border-blue-500"
           :icon (icon-svg :audio)}
   :default {:background "bg-indigo-500"
             :border "border-indigo-500"
             :icon (icon-svg :sticker)}
   })

(defn thumbnail-uri [state asset]
  (extension-points/asset-thumbnail {:state state :asset asset}))

(defn attributes-by-type
  [state {:keys [asset/type asset/uri] :as asset}]
  (let [base (get type->style type (:default type->style))]
    (cond-> base
            (#{:image :gif} type)
            (assoc :background-image (str "url(" uri ")"))

            (= type :video)
            (assoc :background-image (str "url(" (thumbnail-uri state asset)
                                          ") 0 0 / auto 100% repeat-x"))

            (= type :audio)
            (assoc :background-image "url('/img/editor2/waveform.png')")

            (#{:hashtag-sticker
               :countdown-sticker
               :poll-sticker
               :mention-sticker
               :link-sticker
               :location-sticker
               :music-sticker
               :question-sticker
               :quiz-sticker
               :emoji-slider-sticker
               :default} type)
            (assoc :background-image (str "url(" (thumbnail-uri state asset)
                                          ") 0 0 / auto 100% repeat-x"))
            )))

(defn track
  [state track-uuid]
  (let [editor2 (:editor2 state)
        pixel-per-second (:pixel-per-second editor2)
        db (:db editor2)
        track (ds/entity db
                         [:track/uuid track-uuid])
        dragged-asset (:clip/asset (common/get-dragged-clip state))
        ;; Sort clips that are in the same track by :clip/start and then by :clip/uuid.
        ;; By :clip/start ensures slideshows with overlapping clips in one track render correctly.
        ;; By :clip/uuid ensures that the server-side render order is deterministic and the same as in the editor.
        clips (sort-by (fn [clip]
                         [(:clip/start clip)
                          (:clip/uuid clip)])
                       (:clip/_track track))]
    [:div
     {:class ["h-8" "bg-gray-800" "flex"]
      :on {:mouseover (when (and (= (:drag (:drag editor2))
                                    :clip-middle)
                                 ;; Prevent clips containing stickers to be dragged onto other tracks.
                                 (not (common/is-sticker? dragged-asset))
                                 ;; Prevent any clips from being moved into tracks containing sticker clips.
                                 (not (common/track-contains-stickers? db track-uuid)))
                        [[:editor2/clip-move-to-track track-uuid]])}}

     ;; Each track row is relative so clips can be absolutely
     ;; positioned:
     [:div.relative
      ;; Render each clip as absolutely positioned
      (for [clip clips]
        (let [{:keys [clip/start
                      clip/end]} clip
              left (ms-to-pixel pixel-per-second
                                start)
              duration (- end
                          start)
              width (ms-to-pixel pixel-per-second
                                 duration)
              selected (= (:selected-clip editor2)
                          (:clip/uuid clip))
              border-radius "0.5rem"
              asset (ds/entity db (:db/id (:clip/asset clip)))
              attr (attributes-by-type state asset)]
          [:div
           {:replicant/key (str "id-" (:clip/uuid clip))
            :class ["absolute" "h-8" "flex" "items-center" "text-xs"
                    "bg-gray-700" "cursor-move" "border-2"
                    (:border attr) "select-none"]
            :style {:left (str left "px")
                    :width (str width "px")
                    :border-radius border-radius
                    :background (str (:background-image attr) " rgba(0, 0, 0, .5)")
                    :background-blend-mode "multiply"
                    :background-size "auto 100%"
                    :background-repeat "repeat-x"
                    :z-index (if selected 10 1)}
            :on {:click [[:editor2/select-clip (:clip/uuid clip)]]
                 ;; Double clicking an AI generated clip opens its
                 ;; model dialog to edit the prompt and regenerate:
                 :dblclick (if (get-in asset [:asset/data :ai/category])
                             [[:openrouter/regenerate
                               {:clip-uuid (:clip/uuid clip)}]]
                             [[:editor2/cycle-overlapping-clips (:clip/uuid clip)]])}}
           (let [marker [:div {:class ["bg-white"
                                       "absolute"
                                       "select-none"]
                               :style {:border-radius "1rem"
                                       :width "0.125rem"
                                       :height "0.625rem"
                                       :left "50%"
                                       :top "50%"
                                       :transform "translate(-50%, -50%)"}}]]
             [:div {:class ["flex" "justify-between" "w-full" "select-none"]}
              ;; Left handle
              [:div {:class ["relative" "h-8" "w-3" (:background attr) "flex-none" "cursor-col-resize"
                             (when-not selected
                               "invisible")]
                     :style {:border-top-left-radius border-radius
                             :border-bottom-left-radius border-radius}
                     :on {:mousedown [[:editor2/drag
                                       {:clip/uuid (:clip/uuid clip)
                                        :drag :clip-left
                                        :start-offset left}]]}}
               marker]

              ;; Center
              [:div {:class ["flex-1" "min-w-0" "select-none"]}
               [:div {:class ["flex" "items-center" "h-8" "ml-4" "overflow-hidden" "text-ellipsis" "whitespace-nowrap"]
                      :on {:mousedown [[:store/assoc-in
                                        [:editor2 :selected-clip]
                                        (:clip/uuid clip)]
                                       [:editor2/drag
                                        {:clip/uuid (:clip/uuid clip)
                                         :drag :clip-middle
                                         :start-offset left}]]}}
                (:icon attr)
                (:clip/name clip)]]

              ;; Mute button
              (when (#{:video :audio} (:asset/type asset))
                [:div {:class ["flex-none" "h-8" "flex" "items-center" "px-1"
                               "cursor-pointer" "opacity-70" "hover:opacity-100"]
                       :title (if (:clip/muted clip) "Unmute" "Mute")
                       :on {:mousedown [[:js/stop-propagation]]
                            :click [[:js/stop-propagation]
                                    [:editor2/toggle-mute (:clip/uuid clip)]]}}
                 (if (:clip/muted clip)
                   [:svg.size-4.text-red-500 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24" :fill "currentColor"}
                    [:path {:d "M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM17.78 9.22a.75.75 0 1 0-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 1 0 1.06-1.06L20.56 12l1.72-1.72a.75.75 0 1 0-1.06-1.06l-1.72 1.72-1.72-1.72Z"}]]
                   [:svg.size-4 {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24" :fill "currentColor"}
                    [:path {:d "M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM18.584 5.106a.75.75 0 0 1 1.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 0 1-1.06-1.06 8.25 8.25 0 0 0 0-11.668.75.75 0 0 1 0-1.06Z"}]
                    [:path {:d "M15.932 7.757a.75.75 0 0 1 1.061 0 6 6 0 0 1 0 8.486.75.75 0 0 1-1.06-1.061 4.5 4.5 0 0 0 0-6.364.75.75 0 0 1 0-1.06Z"}]])])

              ;; Right handle
              [:div {:class ["relative" "h-8" "w-3" (:background attr)
                             "flex-none" "cursor-col-resize"
                             (when-not selected "invisible")]
                     :style {:border-top-right-radius border-radius
                             :border-bottom-right-radius border-radius
                             :margin-right "-1px"}
                     :on {:mousedown [[:editor2/drag
                                       {:clip/uuid (:clip/uuid clip)
                                        :drag :clip-right
                                        :start-offset (+ left
                                                         width)}]]}}
               marker]])
           ]))]]))

(defn divider
  [state after-track]
  [:div
   {:class ["flex" "items-center"
            ;; It is important that the elements stay in the DOM all the time,
            ;; otherwise events like 'mouseup' will not bubble up to the
            ;; `js/document` where the corresponding event handler ends the
            ;; drag.
            (if (and (= after-track
                        (get-in state
                                [:editor2
                                 :divider]))
                     (= (get-in state
                                [:editor2
                                 :drag
                                 :drag])
                        :clip-middle))
              ""
              "opacity-0")
            ]
    :style {:height "9px"}
    :on {:mouseout (when (get-in state
                                 [:editor2 :divider])
                     [[:store/assoc-in
                       [:editor2 :divider]
                       nil]])
         :mouseover (when (= (get-in state
                                     [:editor2
                                      :drag
                                      :drag])
                             :clip-middle)
                      [[:store/assoc-in
                        [:editor2 :divider]
                        after-track]])
         :mouseup [[:editor2/new-track
                    {:after-track after-track
                     :clip/uuid (get-in state
                                        [:editor2
                                         :drag
                                         :clip/uuid])}]]
         }}
   [:div
    {:class ["flex" "justify-center" "w-full" "bg-blue-400" "relative"]
     :style {:height "6px"}}
    ;; divider content:
    [:div
     {:class ["flex" "items-center" "justify-center" "absolute" "bg-blue-400"]
      :style {:top "-9px"
              :width "20px"
              :height "20px"
              :border-radius "50px"}}
     [:svg
      {:width "24", :height "24", :viewBox "0 0 24 24", :fill "none"}
      [:path
       {:d "M12 8V16",
        :stroke "currentColor",
        :stroke-width "1.5",
        :stroke-linecap "round",
        :stroke-linejoin "round"}]
      [:path
       {:d "M16 12H8",
        :stroke "currentColor",
        :stroke-width "1.5",
        :stroke-linecap "round",
        :stroke-linejoin "round"}]]]]])

(def trash-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"}]])

(def scissors-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "m7.848 8.25 1.536.887M7.848 8.25a3 3 0 1 1-5.196-3 3 3 0 0 1 5.196 3Zm1.536.887a2.165 2.165 0 0 1 1.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 1 1-5.196 3 3 3 0 0 1 5.196-3Zm1.536-.887a2.165 2.165 0 0 0 1.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863 2.077-1.199m0-3.328a4.323 4.323 0 0 1 2.068-1.379l5.325-1.628a4.5 4.5 0 0 1 2.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.33 4.33 0 0 0 10.607 12m3.736 0 7.794 4.5-.802.215a4.5 4.5 0 0 1-2.48-.043l-5.326-1.629a4.324 4.324 0 0 1-2.068-1.379M14.343 12l-2.882 1.664"}]])

(def stop-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "#e5e7eb",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z"}]])

(def play-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "#e5e7eb",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"}]])

(def pause-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "M15.75 5.25v13.5m-7.5-13.5v13.5"}]])

(def layer-up-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "m4.5 15.75 7.5-7.5 7.5 7.5"}]])

(def layer-down-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "m19.5 8.25-7.5 7.5-7.5-7.5"}]])

(def collapse-tracks-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"}]])

(def expand-tracks-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-5" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"}]])

;; A helper function to pad a number with leading zeros.
(defn pad
  "Converts number n to a string and pads it on the left with zeros to reach the given width."
  [n width]
  (let [s (str n)]
    (if (< (count s) width)
      (str (apply str (repeat (- width (count s)) "0")) s)
      s)))

(defn format-timecode
  "Formats a time given in milliseconds to the format HH:MM:SS.mmm.
   For example, (format-time 3661000) returns \"01:01:01.000\"."
  [ms]
  (let [hours (quot ms 3600000)
        rem1 (mod ms 3600000)
        minutes (quot rem1 60000)
        rem2 (mod rem1 60000)
        seconds (quot rem2 1000)
        millis (mod rem2 1000)]
    (str (pad hours 2) ":" (pad minutes 2) ":" (pad seconds 2) "." (pad millis 3))))

(defn button-bar
  [state]
  (let [selected-clip (common/get-selected-clip state)
        selected-is-sticker (and selected-clip
                                 (common/is-sticker? (:clip/asset selected-clip)))
        collapsed? (get-in state [:editor2 :timeline-collapsed?])]
    [:div
     {:class ["flex" "justify-between" "border" "rounded-lg" "border-slate-600" "p-2" "bg-gray-800" "select-none"
              (when-not collapsed? "mb-2")]}
     [:div.relative
      (common/tool-tip
        {:element [:button
                   {:class ["hover:bg-gray-900" "p-1" "rounded-md"]
                    :on {:click [[:editor2/cut-clip]]}}
                   scissors-icon]
         :text "Cut clip"
         :position :top})

      (common/tool-tip
        {:element [:button
                   {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-1"]
                    :on {:click [[:editor2/delete-clip]]}}
                   trash-icon]
         :text "Delete clip"
         :position :top})

      (common/tool-tip
        {:element [:button
                   {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-1"]
                    :disabled (or (not selected-clip) selected-is-sticker)
                    :on {:click [[:editor2/move-clip-layer-up]]}}
                   layer-up-icon]
         :text (if selected-is-sticker
                 "Stickers must stay on top"
                 "Move layer up")
         :position :top})

      (common/tool-tip
        {:element [:button
                   {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-1"]
                    :disabled (or (not selected-clip) selected-is-sticker)
                    :on {:click [[:editor2/move-clip-layer-down]]}}
                   layer-down-icon]
         :text (if selected-is-sticker
                 "Stickers must stay on top"
                 "Move layer down")
         :position :top})]

     [:div
      {:class ["flex" "items-center"]}
      [:button
       {:class ["hover:bg-gray-900" "p-1" "rounded-md"]
        :on {:click [[:editor2/stop]]}}
       stop-icon]
      (case (get-in state
                    [:editor2
                     :status])
        :paused
        [:button
         {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-3"]
          :on {:click [[:editor2/play]]}}
         play-icon]

        :playing
        [:button
         {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-3"]
          :on {:click [[:editor2/pause]]}}
         pause-icon]

        [:button
         {:class ["p-1" "rounded-md" "ml-3"]}
         play-icon]
        )
      [:span
       {:class ["ml-3"]}
       (format-timecode (get-in state
                                [:editor2
                                 :playhead-position]))]]

     [:div
      {:class ["flex" "items-center"]}
      [:button
       {:class ["hover:bg-gray-900" "p-1" "rounded-md"]
        :on {:click [[:editor2/timeline-zoom-out]]}}
       common/zoom-out-icon]
      [:button
       {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-1"]
        :on {:click [[:editor2/timeline-zoom-in]]}}
       common/zoom-in-icon]
      (common/tool-tip
        {:element [:button
                   {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-1"]
                    :on {:click [[:store/assoc-in [:editor2 :timeline-collapsed?] (not collapsed?)]]}}
                   (if collapsed?
                     expand-tracks-icon
                     collapse-tracks-icon)]
         :text (if collapsed?
                 "Expand tracks"
                 "Collapse tracks")
         :position :top})]]))

(defn slides
  [state]
  (let [editor2 (:editor2 state)
        pixel-per-second (:pixel-per-second editor2)
        db (:db editor2)
        timeline (ds/entity db
                            [:timeline/uuid
                             (common/q-timeline-uuid db)])
        slide-uuids (:timeline/slides timeline)
        slides (second
                 (reduce
                   (fn [[total slides*] slide]
                     [(+ total
                         (:slide/duration slide))
                      (conj slides*
                            (assoc slide
                                   :slide/start total))])
                   [0 []]
                   (map
                     (fn [slide-uuid]
                       (ds/pull db
                                '[*]
                                [:slide/uuid slide-uuid]))
                     slide-uuids)))]
    [:div
     {:class ["h-6" "bg-gray-800" "flex"]
      :on {:click [[:store/assoc-in
                    [:editor2 :sidebar-panel]
                    :slide]]}}
     [:div.relative
      (map-indexed
        (fn [idx slide]
          (let [left (ms-to-pixel pixel-per-second
                                  (:slide/start slide))
                width (ms-to-pixel pixel-per-second
                                   (:slide/duration slide))
                border-radius "0.5rem"]
            [:div {:class ["absolute" "h-6"  "flex" "items-center" "text-xs"
                           "bg-gray-700" "border-2"
                           "border-yellow-400" "select-none" "cursor-pointer"]
                   :style {:left (str left "px")
                           :width (str width "px")
                           :border-radius border-radius}}
             (let [marker [:div {:class ["bg-white"
                                         "absolute"
                                         "select-none"]
                                 :style {:border-radius "1rem"
                                         :width "0.125rem"
                                         :height "0.625rem"
                                         :left "50%"
                                         :top "50%"
                                         :transform "translate(-50%, -50%)"}}]]
               [:div {:class ["flex" "justify-between" "w-full" "select-none"]}
                [:div.w-full.select-none
                 [:div {:class ["flex" "items-center" "h-6" "ml-4" "overflow-hidden" "select-none"]}
                  (str "Story slide "
                       (inc idx))]]
                [:div {:class ["relative" "h-6" "w-3" "bg-yellow-400" "select-none" "cursor-col-resize"]
                       :style {:border-top-right-radius border-radius
                               :border-bottom-right-radius border-radius
                               :margin-right "-1px"}
                       :on {:mousedown [[:editor2/drag
                                         {:slide/uuid (:slide/uuid slide)
                                          :drag :slide-duration
                                          :old-duration (:slide/duration slide)}]]}}
                 marker
                 ]])]))
        slides)]]))

(defn media-icon [media-selected?]
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-6" (when-not media-selected? "text-slate-400")]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0 1 18 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0 1 18 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 0 1 6 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M19.125 12h1.5m0 0c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h1.5m14.25 0h1.5"}]])


(def x-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-6" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d "M6 18 18 6M6 6l12 12"}]])

(def upload-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-6" "text-slate-400"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"}]])

(def text-icon
  [:svg {:stroke "currentColor",
         :fill "currentColor",
         :xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 448 512"}
   [:path {:d "M254 52.8C249.3 40.3 237.3 32 224 32s-25.3 8.3-30 20.8L57.8 416 32 416c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-1.8 0 18-48 159.6 0 18 48-1.8 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-25.8 0L254 52.8zM279.8 304l-111.6 0L224 155.1 279.8 304z"}]])

(defn q-asset-uuids
  [db]
  (ds/q
    '[:find
      [?asset-uuid ...]
      :where
      [?asset :asset/uuid ?asset-uuid]]
    db))

(defn get-assets
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])]
    (map (fn [asset-uuid]
           (ds/entity db
                      [:asset/uuid asset-uuid]))
         (q-asset-uuids db))))

(defmethod extension-points/asset-thumbnail
  :default
  [_params]
  nil)

(defmethod extension-points/asset-thumbnail
  :image
  [params]
  (:asset/uri (:asset params)))

(defmethod extension-points/asset-thumbnail
  :gif
  [params]
  (:asset/uri (:asset params)))

(defmethod extension-points/asset-thumbnail
  :video
  [params]
  (get-in params
          [:state
           :editor2
           :video-thumbnails
           (:asset/uuid (:asset params))]))

(defmethod extension-points/asset-thumbnail
  :audio
  [_params]
  "/img/thumbnail-audio.svg")

(defn share-menu-item
  [{:keys [label icon on disabled]}]
  [:button {:class ["flex" "flex-col" "items-center" "space-y-1" (if disabled
                                                                   "cursor-not-allowed"
                                                                   "cursor-pointer")]
            :on (when-not disabled
                  on)}
   [:div {:class (concat ["w-10"
                          "h-10"
                          "rounded-full"
                          "flex"
                          "items-center"
                          "justify-center"]
                         (if disabled
                           ["bg-gray-200"]
                           ["bg-gray-300"
                            "hover:bg-gray-400"]
                           ))}
    icon
    ]
   [:span {:class ["text-sm" "text-gray-700" "text-center"]}
    label]])

(def document-icon
  [:svg
   {:xmlns "http://www.w3.org/2000/svg",
    :fill "none",
    :viewBox "0 0 24 24",
    :stroke-width "1.5",
    :stroke "currentColor",
    :class ["size-6"]}
   [:path
    {:stroke-linecap "round",
     :stroke-linejoin "round",
     :d
     "M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"}]])

(def download-icon
  [:svg.size-6 {:xmlns "http://www.w3.org/2000/svg" :fill "none" :viewBox "0 0 24 24" :stroke-width "1.5" :stroke "currentColor"}
   [:path {:stroke-linecap "round" :stroke-linejoin "round" :d "M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"}]])

(def spinner
  [:svg.w-5.h-5.text-gray-300.animate-spin.fill-blue-500 {:aria-hidden "true" :viewBox "0 0 100 101" :fill "none" :xmlns "http://www.w3.org/2000/svg"}
   [:path {:d "M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" :fill "currentColor"}]
   [:path {:d "M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" :fill "currentFill"}]])

(defn empty-design?
  [state]
  (let [db (get-in state
                   [:editor2 :db])
        assets (ds/q '[:find
                       (count ?asset) .
                       :where
                       [?asset :asset/uuid]]
                     db)]
    (or (zero? assets)
        (nil? assets))))

(defn no-changes-made?
  [state]
  (let [editor2-state (:editor2 state)]
    (= (:db-snapshot-hash editor2-state)
       (hash (:db editor2-state)))))

(defn uploading?
  [state]
  (some (fn [[_ upload]]
          (not (:callback-invoked upload)))
        (get-in state
                [:upload-manager :uploads])))

(defn share-menu
  [state]
  [:div {:class ["bg-white"                                 ;; Card background
                 "shadow-md"                                ;; Card shadow
                 "rounded-xl"                               ;; Rounded corners
                 "w-[350px]"                                ;; Fixed width (adjust as needed)
                 "p-4"                                      ;; Card padding
                 "space-y-4"]}                              ;; Vertical spacing between sections

   (let [editor2-state (:editor2 state)
         saving (:saving editor2-state)
         rendering (:rendering editor2-state)]
     [:div.flex.flex-col
      [:div {:class ["space-y-3" "mb-4"]}
       [:h3 {:class ["text-lg" "font-semibold" "text-gray-900" "mb-3"]}
        "Share Your Story"]
       
       [:div {:class ["space-y-2"]}
        [:button
         {:class ["flex" "items-center" "p-3" "bg-blue-50" "border" "border-blue-200" "rounded-lg" "cursor-pointer" "hover:bg-blue-100" "transition-colors" "text-left" "w-full"]
          :on {:click [(if (no-changes-made? state)
                         [:js/location (str "/ui/social-schedule?story-eid="
                                            (:story-media/eid editor2-state))]
                         [:editor2/save! {:on-success-action :editor2/schedule-callback!}])]}
          :disabled (or (empty-design? state)
                        (uploading? state)
                        (:saving editor2-state))}
         [:div {:class ["w-8" "h-8" "bg-blue-500" "text-white" "rounded-full" "flex" "items-center" "justify-center" "text-sm" "font-bold" "mr-3"]}
          [:i.fa-brands.fa-instagram]]
         [:div
          [:div {:class ["font-medium" "text-blue-900"]}
           (cond (uploading? state)
                 "Uploading media..."
                 (:saving editor2-state)
                 "Saving..."
                 :else
                 "Schedule story")]
          [:div {:class ["text-sm" "text-blue-700"]}
           "Share directly to Instagram"]]]

        [:button
         {:class ["flex" "items-center" "p-3" "bg-green-50" "border" "border-green-200" "rounded-lg" "cursor-pointer" "hover:bg-green-100" "transition-colors" "text-left" "w-full"]
          :on {:click (if (no-changes-made? state)
                        [[:story/download-poll {:story-eid (:story-media/eid editor2-state)}]
                         [:store/assoc :ui.editor2.timeline/share-menu-open false]
                         [:ui/modal-open :modal/download {:story-eid (:story-media/eid editor2-state)}]]

                        [[:editor2/save! {:on-success-action :editor2/download-callback!}]])}
          :disabled (or (empty-design? state)
                        (uploading? state)
                        (:saving editor2-state))}
         [:div {:class ["w-8" "h-8" "bg-green-500" "text-white" "rounded-full" "flex" "items-center" "justify-center" "text-sm" "font-bold" "mr-3"]}
          [:i.fa-solid.fa-download]]
         [:div
          [:div {:class ["font-medium" "text-green-900"]}
           (cond (uploading? state)
                 "Uploading media..."
                 (:saving editor2-state)
                 "Saving..."
                 :else
                 "Download & Post Manually")]
          [:div {:class ["text-sm" "text-green-700"]}
           "Download the story and post it yourself"]]]
        ]]
      ])])

(defn navbar
  [state]
  (let [editor2-state (:editor2 state)]
    [:div {:class ["flex"]}
     [:div {:class ["flex" "justify-between" "w-full" "p-3" "border-b" "border-gray-600"]}
      ;; First navbar section
      [:div {:class ["flex" "items-center" "space-x-4"]}
       [:a.flex-shrink-0.flex.items-center.text-white.cursor-pointer.px-4
        {:on {:click [[:editor2/exit (or (empty-design? state)
                                         (no-changes-made? state)
                                         (uploading? state))]]}}
        [:div.block.lg:hidden.h-full.flex.items-center.m-0
         [:i.fas.fa-chevron-left.mr-3]]
        [:div.hidden.lg:block.w-auto.flex.items-center
         [:i.fas.fa-chevron-left.mr-3]
         [:span "Back"]]]

       [:label.inline-flex.items-center.cursor-pointer
        [:input.sr-only.peer
         {:type "checkbox"
          :checked true
          :on {:click [[:editor2/toggle-ig-overlay]]}}]
        [:div.relative.w-11.h-6.bg-gray-200.peer-focus:outline-none.peer-focus:ring-4.peer-focus:ring-blue-300.dark:peer-focus:ring-blue-800.rounded-full.peer.dark:bg-gray-700.peer-checked:after:translate-x-full.rtl:peer-checked:after:-translate-x-full.peer-checked:after:border-white.after:absolute.after:bg-white.after:border-gray-300.after:border.after:rounded-full.after:h-5.after:w-5.after:transition-all.dark:border-gray-600.peer-checked:bg-blue-600.dark:peer-checked:bg-blue-600 {:class "after:content-[''] after:top-[2px] after:start-[2px]"}]
        [:span.ms-3.text-sm.font-medium
         "Instagram Overlay"]]

       [:label.inline-flex.items-center.cursor-pointer
        [:input.sr-only.peer
         {:type "checkbox"
          :checked true
          :on {:click [[:editor2/toggle-snapping]]}}]
        [:div.relative.w-11.h-6.bg-gray-200.peer-focus:outline-none.peer-focus:ring-4.peer-focus:ring-blue-300.dark:peer-focus:ring-blue-800.rounded-full.peer.dark:bg-gray-700.peer-checked:after:translate-x-full.rtl:peer-checked:after:-translate-x-full.peer-checked:after:border-white.after:absolute.after:bg-white.after:border-gray-300.after:border.after:rounded-full.after:h-5.after:w-5.after:transition-all.dark:border-gray-600.peer-checked:bg-blue-600.dark:peer-checked:bg-blue-600
         {:class "after:content-[''] after:top-[2px] after:start-[2px]"}]
        [:span.ms-3.text-sm.font-medium
         "Snapping"]]


       ;; Import from Canva button (shown when there's a design being edited)
       #_(when (get-in state [:editor2 :canva/current-design-id])
         [:button
          {:class ["flex" "items-center" "bg-green-600" "text-white" "px-3" "py-2" "rounded"
                   "hover:bg-green-700"]
           :type "button"
           :on {:click [[:editor2/canva-import-click]]}}
          [:span "Import from Canva"]])]

      ;; Second navbar section
      [:div {:class ["flex" "items-center" "space-x-4"]}    ;; navbar__section
       [:button {:class ["flex" "items-center" "bg-gray-200" "text-black" "px-3" "py-2" "rounded"
                         "disabled:cursor-not-allowed" "disabled:text-gray-700" "disabled:opacity-50"]
                 :on {:click [[:editor2/save!]]}
                 :disabled (or (empty-design? state)
                               (no-changes-made? state)
                               (uploading? state)
                               (common/needs-blurred-images state)
                               (:saving editor2-state)
                               )}
        [:i.fa-solid.fa-floppy-disk]
        [:span
         {:class ["ml-2" "font-bold"]}
         (cond (uploading? state)
               "Uploading media..."
               (:saving editor2-state)
               "Saving..."
               (no-changes-made? state)
               "Saved!"
               :else
               "Save")]]

       [:button {:class ["flex" "items-center" "bg-gray-200" "text-black" "px-3" "py-2" "rounded"
                         "disabled:cursor-not-allowed" "disabled:text-gray-700" "disabled:opacity-50"]
                 :disabled (or (empty-design? state)
                               (uploading? state)
                               (common/needs-blurred-images state)
                               (:saving editor2-state)
                               )
                 :on {:click [[:store/assoc
                               ::share-menu-open
                               (not (::share-menu-open state))]]}}
        [:svg
         {:xmlns "http://www.w3.org/2000/svg"
          :fill "none"
          :viewBox "0 0 24 24"
          :stroke-width "1.5"
          :stroke "currentColor"
          :class ["size-5" "inline"]}
         [:path
          {:stroke-linecap "round"
           :stroke-linejoin "round"
           :d "M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"}]]

        [:span
         {:class ["ml-2" "font-bold"]}
         "Share"]]

       (when (::share-menu-open state)
         (list
           [:div
            {:class ["fixed" "inset-0" "bg-black" "bg-opacity-50" "flex" "items-center" "justify-center"]
             :style {:z-index 9998
             :margin "0px"}
           :on {:click [[:store/assoc ::share-menu-open false]]}}
            ]
           [:div
            {:class ["fixed" "right-0" "top-16" "py-1"]
             :style {:z-index 9999}}
            (share-menu state)]))
       ]]])
  )

(defn loading-panel
  [state]
  [:div {:class ["relative"]}
   [:div
    {:class ["flex" "flex-col" "w-full" "h-full" "overflow-hidden" "overflow-y-hidden" "bg-gray-900" "text-white"]}
    [:div
     {:class ["flex" "flex-col" "grow-0" "shrink-0"]
      :style {:height "calc(70% + 0px)"}}
     (navbar state)
     [:div
      {:class ["flex" "flex-1" "min-h-0" "relative"]}
      ;; sidebar:
      [:div
       {:class ["box-border" "h-full" "pb-3" "bg-gray-900"]
        :style {:z-index 11}}]]]]
   [:div {:class ["absolute" "left-0" "top-0" "w-full" "h-full" "z-10"
                  "backdrop-blur-md" "flex" "items-center" "justify-center" "text-white"]}
    (spinner/vire-spinner-lg)
    [:span.absolute "Loading..."]]])

(defn render
  [state]
  (if-not (get-in state [:editor2 :db])
    (loading-panel state)

    (let [editor2 (:editor2 state)
          db (:db editor2)
          timeline (ds/entity db
                              [:timeline/uuid
                               (common/q-timeline-uuid db)])
          track-uuids (:timeline/tracks timeline)
          collapsed? (:timeline-collapsed? editor2)]
      [:div
       ;; Parent container for the entire timeline.
       ;; Often you want it horizontally scrollable if the timeline is wide:
       {:on {:dragover [[:js/prevent-default]
                        [:js/stop-propagation]]
             :drop [[:js/prevent-default]
                    [:js/stop-propagation]
                    [:editor2/drop-media]]}
        :class ["flex" "flex-col" "w-full" "h-full" "overflow-hidden" "overflow-y-hidden" "bg-gray-900" "text-white"]}

       #_[:div.h-16
          (pr-str (dissoc (:editor2 state)
                          :db))
          (pr-str track-uuids)
          (when-let [selected (:selected-clip editor2)]
            (ds/pull db
                     '[*]
                     [:clip/uuid selected]))]

       ;; layout-content:
       [:div
        {:class ["flex" "flex-col" "grow-0" "shrink-0" "transition-all" "duration-300" "ease-in-out"]
         :style {:height (if collapsed?
                           "calc(100% - 52px)"
                           "calc(70% + 0px)")}}
        ;; layout navbar:
        (navbar state)
        ;; layout container:
        [:div
         {:class ["flex" "flex-1" "min-h-0" "relative"]}
         ;; sidebar:
         [:div
          {:class ["box-border" "h-full" "pb-3" "bg-gray-900"]
           :style {:z-index 11}}
          ;; sidebar content:

          (let [selected (get-in state
                                 [:editor2
                                  :sidebar-panel])]
            [:div
             {:class ["box-border" "flex" "flex-col" "gap-2" "h-full" "overflow-y-auto" "pt-6" "pb-6" "pl-2" "pr-2" "w-24"]}


             (let [media-selected? (= selected :media)]
               [:div
                {:class ["flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center" "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"
                         (when media-selected?
                           "bg-gray-800")]
                 :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :media]]}}
                (media-icon media-selected?)
                [:span
                 {:class ["text-center" "text-xs" "font-medium" (when-not media-selected? "text-slate-400")]}
                 "Media"]])

             ;; sidebar content button:
             (sidebar-panel-sticker/sticker-entry (= selected :sticker))
             (sidebar-canva/entry (= selected :canva))
             (sidebar-text-presets/entry (= selected :text-presets))
             (openrouter-panel/ai-entry (= selected :openrouter))
             (sidebar-images/images-entry (= selected :images))
             (sidebar-giphy/gif-entry (= selected :gif-sidebar))
             (sidebar-hyperframes/entry (= selected :hyperframes-templates))
             (sidebar-captions/entry (= selected :captions-sidebar))
             (sidebar-emojies/entry (= selected :emojies))
             (sidebar-background/entry (= selected :background))

             ])]

         (when (:sidebar-panel editor2)
           ;; sidebar panel:
           [:div
            {:class ["h-full" "left-24" "absolute" "top-0" "max-w-[300px]" "w-[70vw]" "bg-transparent" "z-20"]}
            (sidebar/panel state)])
         (composition/composition state)]]

       [:div
        {:tabindex 0
         :id "timeline"
         :class ["flex" "flex-col" "bg-gray-900" "text-white"
                 (if collapsed? "p-2" "p-4")
                 "min-h-0" "transition-all" "duration-300" "ease-in-out"]
         :style (when-not collapsed?
                  {:max-height "calc(100vh / 3)"})}
        (button-bar state)
        (when-not collapsed?
          [:div.relative.overflow-x-auto.overflow-y-hidden.flex.flex-col
           [:div
            (time-selector state)]
           (timeline-playhead state)
           [:div
            {:class ["overflow-y-auto" "overflow-x-hidden" "pb-5" "min-w-full"]
             :style {:width (str (+ (get-timeline-width state)
                                    ;; For the scrollbar:
                                    20)
                                 "px")}}
            (slides state)
            (divider state
                     :first)
            (for [track-uuid track-uuids]
              (list
                (track state
                       track-uuid)
                (divider state
                         track-uuid)))]])]])
    ))

(def example-tx
  [{:db/id "main-video"
    :asset/uuid #uuid "47ba23df-4e7f-4208-881f-b4f3d08381f1"
    :asset/type :video
    :asset/uri "http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
    :asset/name "1526909-hd_1280_720_24fps.mp4"
    :asset/width 1280
    :asset/height 720
    :asset/duration (* 1000 10)}

   {:db/id "background-music"
    :asset/uuid #uuid "65fce473-452b-4508-93d8-c6eecd48a32a"
    :asset/type :audio
    :asset/uri "https://cdn.pixabay.com/audio/2025/03/04/audio_24c6849ea8.mp3"
    :asset/name "background-music.mp3"
    :asset/duration (* 1000 30)}

   {:db/id "intro-sound-asset"
    :asset/uuid #uuid "b90ab0f6-98f8-489d-b1d5-959011740795"
    :asset/type :audio
    :asset/uri "https://cdn.pixabay.com/audio/2024/09/19/audio_09e4332d21.mp3"
    :asset/name "intro-sound.mp3"
    :asset/duration 7080}

   {:db/id "image"
    :asset/uuid #uuid "3a07899f-6cd2-4848-a83b-337c284b204c"
    :asset/type :image
    :asset/uri "https://placebear.com/800/600"
    :asset/name "800x600-placeholder.jpg"
    :asset/width 800
    :asset/height 600

    ;;"blob:1ab0918a-903d-46d0-a357-4a18d04e05f8"
    }

   {:db/id "second-image"
    :asset/uuid #uuid "0b9081d9-045b-4224-8253-69af72f394ec"
    :asset/type :image
    :asset/uri "https://placebear.com/900/600"
    :asset/name "900x600-placeholder.jpg"
    :asset/width 900
    :asset/height 600

    ;;"blob:1ab0918a-903d-46d0-a357-4a18d04e05f8"
    }

   {:db/id "cat-gif-video"
    :asset/uuid #uuid "25bdabd3-9e76-498a-b714-e939405c5645"
    :asset/type :video
    :asset/uri "https://media1.giphy.com/media/lJNoBCvQYp7nq/giphy.mp4?cid=b100373enztydndbd4mxvmm0a8vzkl2pef2ra6y54n2f9i67&ep=v1_gifs_search&rid=giphy.mp4&ct=g"
    :asset/name "cat.mp4"
    :asset/width 480
    :asset/height 480
    :asset/duration 1000}


   {:db/id "cat-gif"
    :asset/uuid #uuid "20a65e1b-07c7-415a-b512-e2e78ccaaddc"
    :asset/type :gif
    :asset/uri "https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExdzhqcTVhMnh6Nmlzbm1zcGprcThuYmtkMmJyY21mdGllaThrNzZwNiZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/iEbPnIPyh9Exq/giphy.gif"
    :asset/name "cat.gif"
    :asset/width 288
    :asset/height 315
    :asset/duration 1300}

   {:clip/uuid #uuid "d8a0da8e-9682-4f17-9d9e-99f0ef31f464"
    :clip/asset "second-image"
    :clip/name "second image"
    :clip/start 1500
    :clip/end 2500
    :clip/transform {:translate [104.4 810.8 0]}
    :clip/track "image-track"}

   {:clip/uuid #uuid "8134e805-6bf8-431e-8a10-a542285e1bdb"
    :clip/asset "image"
    :clip/name "cover image"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [104.4 210.8 0], :scale [1.1159999999999999 1.116 1] :rotate 45}
    :clip/track "video-track"}

   {:clip/uuid #uuid "aa444392-b13a-4e03-8718-9e33b679cdb1"
    :clip/asset "main-video"
    :clip/name "Main Video"
    :clip/start 2000
    :clip/end 8000
    :clip/offset 20000
    :clip/transform {:translate [104.4 820 0], :scale [1.1159999999999999 1.116 1] :rotate -20}
    :clip/track "video-track"}

   {:clip/uuid #uuid "bdbfdc6c-c3aa-419b-86d6-4ef6f80d1461"
    :clip/asset "cat-gif-video"
    :clip/name "Cat GIF Video"
    :clip/start 0
    :clip/end 8000
    :clip/offset 0
    :clip/loop true
    :clip/transform {:translate [104.4 1420 0], :scale [1.1159999999999999 1.116 1] :rotate 20}
    :clip/track "clip-track"}

   {:clip/uuid #uuid "7b8b2f03-7c1d-465a-a0de-b3e1e458c4b7"
    :clip/asset "cat-gif"
    :clip/name "Cat GIF"
    :clip/start 0
    :clip/end 4000
    :clip/offset 0
    :clip/loop true
    :clip/transform {:translate [904.4 20 0], :scale [1.1159999999999999 1.116 1] :rotate 20}
    :clip/track "gif-track"}

   {:clip/uuid #uuid "c158a0a2-a164-43a8-8a34-cba6dc77774f"
    :clip/asset "background-music"
    :clip/name "Background Music"
    :clip/start 0
    :clip/end 8000
    :clip/offset 4000
    :clip/track "audio-track"}

   {:clip/uuid #uuid "dc864fd5-08d7-4f8f-9f1e-f095dbd4ee9c"
    :clip/asset "intro-sound-asset"
    :clip/name "Intro Sound"
    :clip/start 2000
    :clip/end 4000
    :clip/offset 1000
    :clip/track "intro-sound-track"}

   {:db/id "video-track"
    :track/uuid #uuid "0224ce8f-7a7d-4a93-b6e8-99b0a8e4f7e7"
    :track/name "main track"}

   {:db/id "clip-track"
    :track/uuid #uuid "01ce83f4-7f63-48ab-8138-0783ac7a6b94"
    :track/name "clip track"}

   {:db/id "image-track"
    :track/uuid #uuid "a0a4445e-5d59-4427-bd37-c10a46b58868"
    :track/name "image track"}

   {:db/id "gif-track"
    :track/uuid #uuid "4cb10b8c-5269-4658-adfe-7def55a9ddc8"
    :track/name "gif track"}

   {:db/id "audio-track"
    :track/uuid #uuid "6833769f-d209-4357-953f-7f0a45a49481"
    :track/name "audio track"}

   {:db/id "intro-sound-track"
    :track/uuid #uuid "c285c014-86db-4ebd-95c0-bd2ffdb257cb"
    :track/name "intro sound track"}

   {:timeline/uuid #uuid "d2be491c-4781-4d74-bd08-36636875047a"
    :timeline/name "My summer story"
    :timeline/tracks [#uuid "36686d46-9fa6-4508-a723-62f4c58d2677"
                      #uuid "afadd945-9f4d-4892-b776-da4d11c6fa58"
                      #uuid "19b70c50-f820-4c00-95b3-b6a2da09e149"
                      #uuid "bb20f1fe-f585-4795-be1f-d4d4ad3498d2"
                      #uuid "83c8e109-5b96-453d-8c0a-2dd1c0e602ed"
                      #uuid "cf4ba027-674a-4804-b174-9f650dc91749"
                      #uuid "e402ac94-6e2e-4bd1-bdca-a85acb7392e2"
                      #uuid "01ce83f4-7f63-48ab-8138-0783ac7a6b94"
                      #uuid "4cb10b8c-5269-4658-adfe-7def55a9ddc8"
                      #uuid "0224ce8f-7a7d-4a93-b6e8-99b0a8e4f7e7"
                      #uuid "a0a4445e-5d59-4427-bd37-c10a46b58868"
                      #uuid "c285c014-86db-4ebd-95c0-bd2ffdb257cb"
                      #uuid "6833769f-d209-4357-953f-7f0a45a49481"]
    :timeline/slides [#uuid "ce4f1d8a-a3c2-4d8b-a502-5ef74d7e4971"
                      #uuid "fcf4cc18-1511-4e62-9dcc-aad81cb59943"]}

   {:slide/uuid #uuid "ce4f1d8a-a3c2-4d8b-a502-5ef74d7e4971"
    :slide/duration 2000
    :slide/background-color "#1678d4"}
   {:slide/uuid #uuid "fcf4cc18-1511-4e62-9dcc-aad81cb59943"
    :slide/duration 6000
    :slide/background-color "#16d471"}

   {:db/id "poll-sticker-asset"
    :asset/uuid #uuid "880daa3e-995e-49a5-be66-da36626312a0"
    :asset/type :poll-sticker
    :asset/name "Poll Sticker"
    :asset/data {:question "THIS IS A RATHER LONG QUESTION AND I HAD SOME MICRO SD CARDS"
                 :tallies [{:text "Yes"
                            :font_size 42}
                           {:text "No"
                            :font_size 42}]
                 :color "green"}}

   {:clip/uuid #uuid "940675d7-ce51-40f8-8e99-cbf6c81fe797"
    :clip/asset "poll-sticker-asset"
    :clip/name "Poll Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [404.4 1120 0], :scale [0.82 0.82] :rotate -30}
    :clip/track "poll-sticker-track"}

   {:db/id "poll-sticker-track"
    :track/uuid #uuid "36686d46-9fa6-4508-a723-62f4c58d2677"
    :track/name "poll sticker track"}

   {:db/id "hashtag-sticker-asset"
    :asset/uuid #uuid "6cb231a2-cf42-4dde-a67b-6a965fa26bdf"
    :asset/type :hashtag-sticker
    :asset/name "Hashtag Sticker"
    :asset/data {:text "#hashtag",
                 :scaleX 1,
                 :scaleY 1,
                 :top 0,
                 :left 0,
                 :type "hashtag",
                 :design
                 {:text-props
                  {:fontFamily "Instagram Sans Condensed",
                   :fontSize 120,
                   :top 5,
                   :fill "#f10663"},
                  :padding-x 80,
                  :padding-y 40,
                  :rx 20,
                  :ry 20,
                  :opacity 1}}}

   {:clip/uuid #uuid "36604dc6-69d8-4832-b6b1-a9260e9ccd93"
    :clip/asset "hashtag-sticker-asset"
    :clip/name "Hashtag Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [404.4 120 0], :scale [0.82 0.82] :rotate -30}
    :clip/track "hashtag-sticker-track"}

   {:db/id "hashtag-sticker-track"
    :track/uuid #uuid "e402ac94-6e2e-4bd1-bdca-a85acb7392e2"
    :track/name "hashtag sticker track"}

   {:db/id "mention-sticker-asset"
    :asset/uuid #uuid "e5743e9a-904e-496b-ba5c-ae6e3d3fbc7b"
    :asset/type :mention-sticker
    :asset/name "Mention Sticker"
    :asset/data {:text "@maxx80000",
                 :user-id 123,
                 :type "usertag",
                 :design
                 {:text-props
                  {:fontFamily "Instagram Sans Condensed",
                   :fontSize 120,
                   :top 5,
                   :fill "#ff7b00"},
                  :padding-x 80,
                  :padding-y 40,
                  :rx 20,
                  :ry 20,
                  :opacity 1}}}

   {:clip/uuid #uuid "38db88ee-9885-42b3-9010-e66eefebc0dd"
    :clip/asset "mention-sticker-asset"
    :clip/name "Mention Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [104.4 350 0], :scale [0.82 0.82] :rotate 30}
    :clip/track "mention-sticker-track"}

   {:db/id "mention-sticker-track"
    :track/uuid #uuid "cf4ba027-674a-4804-b174-9f650dc91749"
    :track/name "mention sticker track"}

   {:db/id "location-sticker-asset"
    :asset/uuid #uuid "04c80e7e-ea16-4cdb-b20b-eb0edfb654d8"
    :asset/type :location-sticker
    :asset/name "Location Sticker"
    :asset/data {:location {:external_id "115735848437200"
                            :external_id_source "facebook_places"
                            :name "Cologne Germany"
                            :address "4.8mi"
                            :lat 50.939900337381
                            :lng 6.9594646597893}
                 :design {:icon-props {:src "/img/location-v2.svg"
                                       :scaleX 0.2
                                       :scaleY 0.2
                                       :left 20}
                          :text-props {:fill "#7437fa"}
                          :opacity 1.0}}}

   {:clip/uuid #uuid "6c179ea7-b4e3-4c81-bfe2-a1e30a7230ed"
    :clip/asset "location-sticker-asset"
    :clip/name "Location Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [104.4 850 0], :scale [0.82 0.82] :rotate 10}
    :clip/track "location-sticker-track"}

   {:db/id "location-sticker-track"
    :track/uuid #uuid "83c8e109-5b96-453d-8c0a-2dd1c0e602ed"
    :track/name "location sticker track"}

   {:db/id "countdown-sticker-asset"
    :asset/uuid #uuid "b66020f4-7751-4714-907b-5ad834a3321d"
    :asset/type :countdown-sticker
    :asset/name "Countdown Sticker"
    :asset/data {:end_ts (js/Date. (+ (.getTime (js/Date.))
                                      (* 1000 60 60 24 4)))
                 :start_background_color "#ca2ee1"
                 :end_background_color "#5eb1ff"
                 :digit_color "#7e0091"
                 :digit_card_color "#ffffffcc"
                 :text_color "#ffffff"
                 :text "COUNTDOWN🎉"}}

   {:clip/uuid #uuid "abdc93c0-036e-41ac-81a9-167ea2b2e5a9"
    :clip/asset "countdown-sticker-asset"
    :clip/name "Countdown Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [500 1550], :scale [0.82 0.82] :rotate 10}
    :clip/track "countdown-sticker-track"}

   {:db/id "countdown-sticker-track"
    :track/uuid #uuid "bb20f1fe-f585-4795-be1f-d4d4ad3498d2"
    :track/name "countdown sticker track"}

   {:db/id "question-sticker-asset"
    :asset/uuid #uuid "60b7d466-0735-496b-a64b-ba0da0dc1671"
    :asset/type :question-sticker
    :asset/name "Question Sticker"
    :asset/data {:question "Question?"
                 :background-color "#a207ba"
                 :text-color "#ffffff"}}

   {:clip/uuid #uuid "19856a6f-41e5-41e8-9565-8c9c298f616e"
    :clip/asset "question-sticker-asset"
    :clip/name "Question Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [550 250], :scale [0.82 0.82] :rotate 10}
    :clip/track "question-sticker-track"}

   {:db/id "question-sticker-track"
    :track/uuid #uuid "19b70c50-f820-4c00-95b3-b6a2da09e149"
    :track/name "question sticker track"}

   {:db/id "quiz-sticker-asset"
    :asset/uuid #uuid "1632d12e-eb40-432a-92ef-9029572e021f"
    :asset/type :quiz-sticker
    :asset/name "Quiz Sticker"
    :asset/data {:text_color "#ffffff"
                 :start_background_color "#262626"
                 :end_background_color "#262626"
                 :question "GUESS MY FAVORITE..."
                 :options ["A" "B"]
                 :correct_answer 0}}

   {:clip/uuid #uuid "b1e30522-90b3-40e1-8828-818e61186908"
    :clip/asset "quiz-sticker-asset"
    :clip/name "Quiz Sticker"
    :clip/start 0
    :clip/end 2000
    :clip/transform {:translate [1 1150], :scale [0.5 0.5] :rotate 0}
    :clip/track "quiz-sticker-track"}

   {:db/id "quiz-sticker-track"
    :track/uuid #uuid "afadd945-9f4d-4892-b776-da4d11c6fa58"
    :track/name "quiz sticker track"}
   ])

(defn new-empty-design-tx
  []
  (let [slide-uuid (random-uuid)
        timeline-uuid (random-uuid)]
    [{:slide/uuid slide-uuid
      :slide/duration 15000}
     {:timeline/uuid timeline-uuid
      :timeline/name "My story"
      :timeline/slides [slide-uuid]}]))

(defn init-db
  [tx]
  (-> (ds/empty-db)
      (ds/with-schema schema/schema)
      (common/transact-db tx)))

(defn- parse-int-param
  "The query parameter `k` as an integer, nil when it is missing or no
   number. A link `?editor1-import=undefined` reached the editor on
   2026-10-16 and `js/parseInt` turned it into NaN, which was then
   requested as `?story-media-eid=NaN` (STORRITO-V2-MB)."
  [query-params k]
  (when-let [value (get query-params k)]
    (let [n (js/parseInt value 10)]
      (when-not (js/isNaN n)
        n))))

(defn- scheduled-post-params
  [query-params]
  (cond-> {}
    (:story-post-eid query-params)
    (assoc :story-post/eid (parse-int-param query-params :story-post-eid))

    (:tiktok-post-eid query-params)
    (assoc :tiktok-post/eid (parse-int-param query-params :tiktok-post-eid))))

(defn on-load-actions
  "The actions that initialize the editor for the `query-params` of the
   page.

   The `:editor1-import` handles every gallery media without an editor2
   design, also a freshly uploaded video or image, and takes seconds.
   Meanwhile `[:editor2 :db]` stays empty, so that `render` shows the
   `loading-panel`. An editable placeholder design would be replaced
   when the import arrives, together with everything the user added to
   it in the meantime."
  [query-params]
  (let [post-params (scheduled-post-params query-params)]
    (cond (:editor1-import query-params)
          [[:store/assoc :editor2 nil]
           [:editor2/editor1-import
            (merge {:story-media/eid (parse-int-param query-params :editor1-import)}
                   post-params)]
           [:editor2/register-hotkeys]]

          (:story-media-eid query-params)
          [[:editor2/load
            (merge {:story-media/eid (parse-int-param query-params :story-media-eid)
                    :design-blob/id (:design-blob-id query-params)}
                   post-params)]
           [:editor2/register-hotkeys]]

          :else
          (let [tx (cond
                     (:dev query-params)
                     example-tx

                     :else
                     (new-empty-design-tx)
                     )

                db (init-db tx)]
            [[:store/assoc :editor2
              (merge defaults/defaults
                     {:db db})]
             [:editor2/prepare-assets!]
             [:editor2/register-hotkeys]
             ]))))

(def page
  {:page-id :pages/editor2
   :route [["ui" "editor2"]]
   :on-load (fn [location]
              (.push js/window.$crisp
                     (clj->js ["do" "chat:hide"]))
              (on-load-actions (:location/query-params location)))
   :render #'render})
